/* eslint-disable */
// BBOT — Phase D capture guide (player-facing, surfaced in the upload flow). Tiered camera placement on a
// baseball-diamond-from-above, honest per-tier expectations (BBOT shows a bar only for what your setup
// captured), no-radar framing, and capture cues. Isolated so the App.js wire-in stays tiny. Ported 1:1 from
// the approved prototype; stacked + collapsible for the narrow (520px) upload column.
import React, { useState } from "react";

const BLUE = "#2970b8", CORAL = "#e8526a", INK2 = "#6a7da6", HAIR = "#dbe6fb", GREEN = "#2f9e6f";

// camera positions on the 410x380 diamond
const CAMS = {
  side:  {x:62,  y:235, c:BLUE,  label:"Side-on"},
  plate: {x:285, y:356, c:CORAL, label:"Behind plate"},
  line:  {x:356, y:300, c:BLUE,  label:"1B line"},
  field: {x:360, y:150, c:CORAL, label:"Field angle"},
};
const TIERS = [
  {n:1, name:"1 camera", cams:["side"],
   note:"One camera, placed SIDE-ON (perpendicular to the player) — the single most valuable angle, the one that sees the body mechanics BBOT measures. A behind-the-plate phone also has value, but it can't see side-on mechanics.",
   yes:["Every repetition verified to the player, at its own moment","The shape of his movement across the session — relative only, no units claimed","Pitch / throw / exit velocity — ONLY if a radar reads in the frame"],
   no:["Pop time & catcher exchange (needs a plate view)","Home-to-first run timing (needs a baseline view)","Multi-angle confirmation"]},
  {n:2, name:"2 cameras", cams:["side","plate"],
   note:"Side-on + behind-the-plate. The common 'two phones' setup — side-on carries the mechanics; the plate angle adds the catcher and pitch view.",
   yes:["Everything from 1 camera","Catcher pop time & exchange","Cleaner delivery confirmation from a second viewpoint"],
   no:["Home-to-first run timing (needs a baseline view)","Full field / spray angles"]},
  {n:3, name:"3 cameras", cams:["side","plate","line"],
   note:"Side-on + behind-plate + down the first-base line. Adds the running game.",
   yes:["Everything from 2 cameras","Home-to-first run timing down the line"],
   no:["Full field / spray defensive angles"]},
  {n:4, name:"4 cameras", cams:["side","plate","line","field"],
   note:"The full static rig — every angle. BBOT measures the most tools it can from one session.",
   yes:["Everything from 3 cameras","Field / spray defensive angle","Fullest multi-angle measurement"],
   no:["Bat/ball-tracking metrics (bat speed, launch, spin) — a separate future capture, not an angle"]},
];
const CUES = [
  // THE FIRST CUE IS A MEASUREMENT, NOT AN INSTRUCTION IN ETIQUETTE.
  // Every body-relative number BBOT reports (stride, weight shift, head movement) is a length
  // divided by the player's own standing height IN PIXELS -- so it needs one frame where he is
  // actually standing, filmed by the SAME camera at the SAME distance as the session. Without it
  // the scale has to be guessed from somewhere else in the clip, and on the first session we tried
  // that on, "somewhere else" turned out to be the intro title card: a closer camera, a 40% error,
  // and no way to know from the footage. Two seconds of standing still is the cheapest measurement
  // in the whole product, and the customer is the only one who can film it.
  ["Start standing","begin with the player standing straight to camera for two seconds — this is what BBOT measures his proportions against"],
  ["Steady","tripod or propped — no panning or zooming"],
  ["Full body","player fills the frame, head to feet"],
  ["1080p or better","60fps where available — it doubles timing precision"],
  // ADMIN NOTE — FOOTAGE TRUTH, NOT FAILURE (2026-08-11). Toe-tap hitters (lead foot lifts and
  // sets down in place, no translating stride) have no detectable plant at 30fps, so the
  // plant-anchored metrics -- stride, weight shift, head movement, swing tempo -- are unavailable
  // for them regardless of lighting or angle. Nothing to fix in the filming; it is the mechanic.
  ["Even lighting","avoid heavy backlight / deep shadow"],
  // This cue is about PRESENCE and ORIENTATION rather than distance, and that ordering came from
  // measurement rather than intuition -- the measurement itself is recorded backend-side, not here.
  // THIS FILE SHIPS TO THE BROWSER, so it carries no thresholds, no sample rates and no internal
  // figures (CLAUDE.md 3). A capture cue tells the customer what to do; it must not tell a
  // competitor what BBOT can and cannot see, or where its limits sit.
  ["Radar in shot","if you're using a gun, keep it in frame and facing the camera the whole session, and let each reading sit on screen before you move it — BBOT reads the number off the display, and a screen turned edge-on reads as nothing at all"],
  ["One subject","just the player in frame"],
  ["One continuous clip","NOT an edited multi-angle reel — those distort the view and get discarded"],
];

function CamIcon({cam, on}){
  return (
    <g transform={"translate("+cam.x+","+cam.y+")"} opacity={on?1:0.16}>
      <circle r="15" fill={cam.c}/>
      <rect x="-7" y="-5" width="14" height="10" rx="2" fill="#fff"/>
      <circle cx="2" cy="0" r="3" fill={cam.c}/>
      <rect x="-10" y="-3.5" width="4" height="7" rx="1.5" fill="#fff"/>
      {on && <text y="-21" textAnchor="middle" fontFamily="Inter,Arial" fontWeight="800" fontSize="12" fill={cam.c}>{cam.label}</text>}
    </g>
  );
}
function Diamond({active}){
  return (
    <svg viewBox="0 0 410 380" style={{width:"100%",maxWidth:340,display:"block",margin:"0 auto"}}>
      <rect x="0" y="0" width="410" height="380" rx="14" fill="#f3f7ff"/>
      <path d="M45 300 A205 205 0 0 1 365 300" fill="#eaf0fc" stroke={HAIR} strokeWidth="2"/>
      <polygon points="205,150 290,235 205,320 120,235" fill="#fbe9d2" stroke="#e3c79c" strokeWidth="2"/>
      <polygon points="205,178 268,235 205,300 142,235" fill="#dff0db" stroke="#b9dcb0" strokeWidth="1.5"/>
      <circle cx="205" cy="235" r="9" fill="#fbe9d2" stroke="#e3c79c" strokeWidth="2"/>
      <rect x="198" y="313" width="14" height="14" fill="#fff" stroke={BLUE} strokeWidth="2" transform="rotate(45 205 320)"/>
      <text x="205" y="340" textAnchor="middle" fontFamily="Inter,Arial" fontSize="9" fontWeight="700" fill={INK2}>HOME</text>
      {Object.keys(CAMS).map(function(k){ return <CamIcon key={k} cam={CAMS[k]} on={active.indexOf(k)>=0}/>; })}
    </svg>
  );
}

export function BbotCaptureGuide(){
  var [open, setOpen] = useState(true);
  var [idx, setIdx] = useState(function(){   // default Tier 2 (common two-phones); ?tier=N deep-links a tier
    var m = (typeof window !== "undefined") && (window.location.search.match(/tier=(\d)/));
    return m ? Math.max(0, Math.min(3, parseInt(m[1], 10) - 1)) : 1;
  });
  var t = TIERS[idx];
  return (
    <div style={{border:"1px solid "+HAIR,borderRadius:12,marginBottom:14,background:"#fff",overflow:"hidden"}}>
      <button onClick={function(){setOpen(!open);}} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 14px",background:"#f3f7ff",border:"none",cursor:"pointer"}}>
        <span style={{font:"800 13px Inter,system-ui,sans-serif",color:BLUE}}>📹 How to film for BBOT</span>
        <span style={{font:"600 11px Inter,system-ui,sans-serif",color:INK2}}>{open?"▾ hide":"▸ show"}</span>
      </button>
      {open && (
        <div style={{padding:"12px 14px"}}>
          <div style={{font:"500 11.5px Inter,system-ui,sans-serif",color:"#33415c",lineHeight:1.5,marginBottom:12}}>
            More angles = more measured. <b>Even one camera gives BBOT real, accurate data — it just measures fewer tools.</b> BBOT shows a bar only for what your setup captured.
          </div>

          {/* tier selector */}
          <div style={{display:"flex",gap:7,marginBottom:12}}>
            {TIERS.map(function(x,i){
              var on = i===idx;
              return (
                <button key={x.n} onClick={function(){setIdx(i);}} style={{flex:1,padding:"7px 4px",border:"1.5px solid "+(on?BLUE:HAIR),background:on?BLUE:"#fff",borderRadius:8,cursor:"pointer",textAlign:"center"}}>
                  <div style={{font:"800 14px Inter,system-ui,sans-serif",color:on?"#fff":BLUE}}>{x.n}</div>
                  <div style={{font:"600 8px Inter,system-ui,sans-serif",color:on?"#fff":INK2}}>{x.n===1?"camera":"cameras"}</div>
                </button>
              );
            })}
          </div>

          <Diamond active={t.cams}/>

          <div style={{font:"800 12px Inter,system-ui,sans-serif",color:BLUE,marginTop:10}}>Tier {t.n} — {t.name}</div>
          <div style={{font:"500 11px Inter,system-ui,sans-serif",color:"#33415c",background:"#f3f7ff",border:"1px solid "+HAIR,borderRadius:8,padding:"7px 10px",margin:"5px 0 11px",lineHeight:1.5}}>{t.note}</div>

          <div style={{font:"800 9.5px Inter,system-ui,sans-serif",color:BLUE,letterSpacing:.5,marginBottom:3}}>MEASURES AT THIS TIER</div>
          {t.yes.map(function(y,i){ return <div key={i} style={{font:"400 11px Inter,system-ui,sans-serif",color:"#33415c",lineHeight:1.5}}><span style={{color:GREEN,fontWeight:800}}>✓</span> {y}</div>; })}
          <div style={{font:"800 9.5px Inter,system-ui,sans-serif",color:CORAL,letterSpacing:.5,margin:"9px 0 3px"}}>NOT CAPTURED AT THIS TIER</div>
          {t.no.map(function(y,i){ return <div key={i} style={{font:"400 11px Inter,system-ui,sans-serif",color:"#33415c",lineHeight:1.5}}><span style={{color:CORAL,fontWeight:800}}>—</span> {y}</div>; })}

          {/* no-radar framing */}
          <div style={{display:"flex",gap:0,margin:"14px 0 0",border:"1px solid "+HAIR,borderRadius:10,overflow:"hidden"}}>
            <div style={{flex:1,padding:"8px 11px",background:"#f3f7ff"}}>
              <div style={{font:"800 10.5px Inter,system-ui,sans-serif",color:BLUE}}>With radar = MPH</div>
              <div style={{font:"400 10px Inter,system-ui,sans-serif",color:"#33415c",marginTop:2,lineHeight:1.45}}>Adds <b>exit, throw & pitch velocity</b>. A <b>handheld gun</b> works too — and if a reading isn't visible on the video, it can be <b>entered by hand</b> and still counts as measured.</div>
            </div>
            <div style={{flex:1,padding:"8px 11px",background:"#fff5f6",borderLeft:"1px solid "+HAIR}}>
              <div style={{font:"800 10.5px Inter,system-ui,sans-serif",color:CORAL}}>No radar? Still real.</div>
              <div style={{font:"400 10px Inter,system-ui,sans-serif",color:"#33415c",marginTop:2,lineHeight:1.45}}>Properly-angled video still gives <b>verified repetitions and the shape of the movement</b>. A narrower report, never a broken one.</div>
            </div>
          </div>

          {/* PITCHER PITCH ZONE — CF angle + 60fps requirement */}
          <div style={{display:"flex",alignItems:"flex-start",gap:8,margin:"9px 0 0",border:"1.5px solid "+CORAL,borderRadius:10,padding:"8px 11px",background:"#fff5f6"}}>
            <span style={{fontSize:15,lineHeight:1.1}}>⚾</span>
            <div style={{lineHeight:1.45}}>
              <span style={{font:"800 10.5px Inter,system-ui,sans-serif",color:CORAL}}>Pitchers: pitch zone mapping (optional feature).</span>
              <span style={{font:"400 10px Inter,system-ui,sans-serif",color:"#33415c"}}> BBOT can map where your pitches cluster (arm-side vs. glove-side, upper vs. lower zone). Requires: <b>CF (center field) angle</b> · <b>60fps minimum</b> · <b>home plate clearly visible</b> in frame. If these aren't met, BBOT skips pitch zone mapping and provides the evaluation without it.</span>
            </div>
          </div>

          {/* BEST for automatic velocity capture: an on-screen radar overlay (value-only framing, no method) */}
          <div style={{display:"flex",alignItems:"flex-start",gap:8,margin:"9px 0 0",border:"1.5px solid "+BLUE,borderRadius:10,padding:"8px 11px",background:"#eef3fc"}}>
            <span style={{fontSize:15,lineHeight:1.1}}>📡</span>
            <div style={{lineHeight:1.45}}>
              <span style={{font:"800 10.5px Inter,system-ui,sans-serif",color:BLUE}}>Best for velocity: an on-screen radar overlay.</span>
              <span style={{font:"400 10px Inter,system-ui,sans-serif",color:"#33415c"}}> When the radar reading shows <b>on the video itself</b>, BBOT captures <b>every</b> velocity automatically — nothing to type. A handheld gun off-screen still works; you just enter those by hand.</span>
            </div>
          </div>

          {/* cues */}
          <div style={{font:"800 9.5px Inter,system-ui,sans-serif",color:BLUE,letterSpacing:.5,margin:"13px 0 4px"}}>EVERY CLIP</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"2px 16px"}}>
            {CUES.map(function(c,i){ return (
              <div key={i} style={{padding:"4px 0",borderTop:"1px solid "+HAIR}}>
                <span style={{color:GREEN,fontWeight:800}}>✓</span> <b style={{font:"700 10.5px Inter,system-ui,sans-serif",color:BLUE}}>{c[0]}</b> <span style={{font:"400 10px Inter,system-ui,sans-serif",color:"#33415c"}}>— {c[1]}</span>
              </div>
            ); })}
          </div>
        </div>
      )}
    </div>
  );
}
